import { VacationDaysSortField, PaginatedVacationDaysQueryVariables } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import SortDropdownFilter from "../../Shared/SortDropdownFilter";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: PaginatedVacationDaysQueryVariables) => void;
}

/**
 * VacationDaysSortingDropdown component
 * Provides a dropdown menu for sorting vacation days data by different fields and orders
 *
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleSubmitClick - Function to handle submitting the updated query variables
 * @returns {JSX.Element} Sorting dropdown component
 */
const VacationDaysSortingDropdown = (props: Props) => {
  const { t } = useTranslation(["translation", "common", "components"]);

  /**
   * Gets user-friendly names for sort fields
   * @param {VacationDaysSortField} field - The sort field to get a display name for
   * @returns {string} User-friendly name for the sort field
   */
  const getSortFieldName = (field: VacationDaysSortField): string => {
    switch (field) {
    case VacationDaysSortField.Days:
      return t("components:filters.vacationdays.sortfields.days");
    case VacationDaysSortField.LengthOfEmployment:
      return t("components:filters.vacationdays.sortfields.lengthofemployment");
    case VacationDaysSortField.ActiveFrom:
      return t("components:filters.vacationdays.sortfields.activefrom");
    case VacationDaysSortField.Province:
      return t("components:filters.vacationdays.sortfields.province");
    default:
      return field;
    }
  };

  const vacationDaysSortConfig = {
    paginationStateKey: "paginatedVacationDays" as keyof PaginationState,
    sortFields: VacationDaysSortField,
    getSortFieldName,
    defaultSortField: VacationDaysSortField.ActiveFrom,
    buildQueryVariables: (displayAmount: number, filters: any, sort: { field: VacationDaysSortField; order: any } | null): PaginatedVacationDaysQueryVariables => ({
      first: displayAmount,
      filters: {
        activeFrom: filters.activeFrom,
        days: filters.days,
        lengthOfEmployment: filters.lengthOfEmployment,
        provinces: filters.provinces,
      },
      sort: sort
    }),
    extractFilters: (paginationState: any) => ({
      activeFrom: paginationState.filters?.activeFrom,
      days: paginationState.filters?.days,
      lengthOfEmployment: paginationState.filters?.lengthOfEmployment,
      provinces: paginationState.filters?.provinces,
    })
  };

  return (
    <SortDropdownFilter
      displayAmount={props.displayAmount}
      handleSubmitClick={props.handleSubmitClick}
      config={vacationDaysSortConfig}
    />
  );
};

export default VacationDaysSortingDropdown;
